import type { I18n, MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { formatList } from "../formatList";

export type DesignSystemDuration = {
	seconds?: number;
	minutes?: number;
	hours?: number;
	days?: number;
	weeks?: number;
};

type FormatDurationParams = {
	duration: DesignSystemDuration | number;
	i18n: I18n;
	locale?: string;
};

const DURATION_UNIT_MESSAGES = {
	weeks: msg({ message: "{count, plural, one {# week} other {# weeks}}" }),
	days: msg({ message: "{count, plural, one {# day} other {# days}}" }),
	hours: msg({ message: "{count, plural, one {# hour} other {# hours}}" }),
	minutes: msg({ message: "{count, plural, =0 {0 minutes} one {# minute} other {# minutes}}" }),
	seconds: msg({ message: "{count, plural, one {# second} other {# seconds}}" }),
} satisfies Record<keyof DesignSystemDuration, MessageDescriptor>;

const renderUnit = ({ i18n, descriptor, count }: { i18n: I18n; descriptor: MessageDescriptor; count: number }) =>
	i18n._(descriptor.id, { count }, { message: descriptor.message });

export const formatDuration = ({ duration, i18n, locale = i18n.locale }: FormatDurationParams) => {
	const durationParts = typeof duration === "number" ? { minutes: duration } : duration;
	let totalMinutes =
		(durationParts.weeks || 0) * 7 * 24 * 60 +
		(durationParts.days || 0) * 24 * 60 +
		(durationParts.hours || 0) * 60 +
		(durationParts.minutes || 0);
	const weeks = Math.floor(totalMinutes / (7 * 24 * 60));
	totalMinutes %= 7 * 24 * 60;
	const days = Math.floor(totalMinutes / (24 * 60));
	totalMinutes %= 24 * 60;
	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;

	const parts: string[] = [];
	if (weeks) {
		parts.push(renderUnit({ i18n, descriptor: DURATION_UNIT_MESSAGES.weeks, count: weeks }));
	}
	if (days) {
		parts.push(renderUnit({ i18n, descriptor: DURATION_UNIT_MESSAGES.days, count: days }));
	}
	if (hours) {
		parts.push(renderUnit({ i18n, descriptor: DURATION_UNIT_MESSAGES.hours, count: hours }));
	}
	if (minutes) {
		parts.push(renderUnit({ i18n, descriptor: DURATION_UNIT_MESSAGES.minutes, count: minutes }));
	}
	if (durationParts.seconds) {
		parts.push(renderUnit({ i18n, descriptor: DURATION_UNIT_MESSAGES.seconds, count: durationParts.seconds }));
	}
	if (parts.length === 0) {
		return renderUnit({ i18n, descriptor: DURATION_UNIT_MESSAGES.minutes, count: 0 });
	}
	return formatList({ values: parts, locale });
};
